// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import {
  Bar, BarChart, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CHART_COLORS_LIGHT as CHART_COLORS } from 'Utils/chartColors';

// The per-feature popup chart spec Khora bakes onto each feature (the `khora_chart` JSON string).
export interface SpatialChartSpec {
  chart_type: string;
  title?: string;
  data: { name: string; value: number | string | null }[];
}

interface SpatialFeatureChartProps {
  spec: SpatialChartSpec;
  width?: number;
  height?: number;
}

/**
 * A small FIXED-SIZE recharts chart of ONE feature's own values, for its map popup. Prop-only and
 * declarative — rendered as a React child of PopupWindow, never as Esri native popup HTML. recharts
 * draws its labels/tooltips as escaped SVG <text>, so there is no HTML-injection surface.
 */
const SpatialFeatureChart: React.FC<SpatialFeatureChartProps> = ({ spec, width = 260, height = 150 }) => {
  // Coerce values to finite numbers (the Lambda may bake a string/null attribute onto a feature); drop
  // rows that aren't plottable so recharts' numeric axes / pie angles never receive NaN or null.
  const rows = (spec?.data || [])
    .filter((row) => row && row.name != null)
    .map((row) => ({ name: String(row.name), value: Number(row.value) }))
    .filter((row) => Number.isFinite(row.value));
  if (!rows.length) return null;
  const axisTick = { fontSize: 10 };

  const renderBody = (): React.ReactElement => {
    if (spec.chart_type === 'pie' || spec.chart_type === 'donut') {
      return (
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" innerRadius={spec.chart_type === 'donut' ? 34 : 0} outerRadius={56}>
            {rows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Cell key={`${row.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 9 }} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
        </PieChart>
      );
    }
    if (spec.chart_type === 'line' || spec.chart_type === 'area') {
      return (
        <LineChart data={rows}>
          <XAxis dataKey="name" tick={axisTick} />
          <YAxis tick={axisTick} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} dot={false} />
        </LineChart>
      );
    }
    return (
      <BarChart data={rows}>
        <XAxis dataKey="name" tick={axisTick} />
        <YAxis tick={axisTick} />
        <Tooltip cursor={false} contentStyle={{ fontSize: 11 }} />
        <Bar dataKey="value">
          {rows.map((row, index) => (
            <Cell key={String(row.name)} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    );
  };

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">{renderBody()}</ResponsiveContainer>
    </div>
  );
};

export default SpatialFeatureChart;
